class WistiaVideo extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    window._wq = window._wq || [];
    this.wistiaLoading = false;
    this.wistiaLoaded = false;
    this.wistiaInstance = null;
    this.mode = this.getAttribute('mode');
    this.autoplay = this.getAttribute('autoplay') === 'true';
    this.playTrigger = this.querySelector('[data-play-trigger]');
    this.placeholder = this.querySelector('[data-placeholder]');
    this.video = this.getAttribute('video');

    if (!this.video) {
      return;
    }

    this.videoEmbedId = `wistia_${this.video}-${Math.random().toString(36).substring(2, 15)}`;
    this.videoEmbed = document.createElement('div');
    this.videoEmbed.id = this.videoEmbedId;
    this.videoEmbed.classList.add('wistia_embed', `wistia_async_${this.video}`);

    if (this.mode === 'inline') {
      this.videoEmbed.classList.add('autoPlay=true');
    }

    if (this.mode === 'popover') {
      this.videoEmbed.classList.add('popover=true', 'popoverContent=html');
    }

    _wq.push({
      id: this.videoEmbedId, onReady: (video) => {
        this.wistiaInstance = video;
        const quarterPlayed = Math.floor(video.duration() / 4),
          halfPlayed = Math.floor(video.duration() / 2),
          threeQuartersPlayed = quarterPlayed * 3;

        video.bind('play', () => {
          this.trackWistiaPlaysInHeap(video, 0);
        });

        /* Track quarters watched */
        video.bind('secondchange', (s) => {
          if (s === quarterPlayed) {
            this.trackWistiaPlaysInHeap(video, 0.25);
          }

          if (s === halfPlayed) {
            this.trackWistiaPlaysInHeap(video, 0.5);
          }

          if (s === threeQuartersPlayed) {
            this.trackWistiaPlaysInHeap(video, 0.75);
          }
        });

        video.bind('end', function () {
          trackWistiaPlaysInHeap(video, 1);
        });

        if (this.mode === 'popover') {
          video.popover.show();
          video.play();
        }
      }
    });

    this.checkWistia().then((wistiaLoaded) => {
      this.wistiaLoaded = wistiaLoaded;
    });

    this.playTrigger.addEventListener('click', this.play.bind(this));

    if (!this.autoplay && this.placeholder && this.placeholder.classList.contains('is--temp')) {
      this.loadPlaceholder();
    }

    if (this.autoplay) {
      this.initAutoplay();
    }
  }

  initAutoplay() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.play().then(() => {
          });
          observer.unobserve(entry.target);
        }
      });
    });

    observer.observe(this);
  }

  async play() {
    if (!this.wistiaLoaded) {
      this.wistiaLoaded = await this.checkWistia();
    }

    if (this.wistiaInstance) {
      if (this.wistiaInstance.popover) {
        this.wistiaInstance.popover.show();
      }

      this.wistiaInstance.play();
      return;
    }

    this.append(this.videoEmbed);

    if (this.mode === 'inline') {
      this.placeholder.classList.add('hidden');
    }

    if (!this.wistiaLoaded) {
      this.playTrigger.classList.add('is-loading');
      await this.loadWistia();
    }

    if (this.mode === 'inline') {
      this.playTrigger.classList.add('hidden');
    } else {
      this.playTrigger.classList.remove('is-loading');
    }
  }

  async loadWistia() {
    if (this.wistiaLoading) {
      return this.checkWistia();
    }

    this.playTrigger.classList.add('is-loading');
    const wistiaScript = document.createElement('script');
    wistiaScript.src = `https://fast.wistia.com/assets/external/E-v1.js`;
    wistiaScript.async = true;
    document.body.appendChild(wistiaScript);

    return new Promise((resolve) => {
      wistiaScript.addEventListener('load', () => {
        this.wistiaLoading = false;
        this.wistiaLoaded = true;

        resolve(true);
      });
    });
  }

  async checkWistia() {
    if (typeof Wistia !== 'undefined') {
      return true;
    }

    const wistiaScript = document.querySelector('script[src*="fast.wistia.com/assets/external/E-v1.js"]');

    if (wistiaScript) {
      this.wistiaLoading = true;

      await new Promise((resolve) => {
        wistiaScript.addEventListener('load', () => {
          this.wistiaLoading = false;
          resolve(true);
        });
      })
    }

    return false;
  }

  async loadPlaceholder() {
    if (this.placeholder) {
      const placeholderImage = this.placeholder.querySelector('img');

      if (!placeholderImage) {
        return;
      }

      this.placeholder.classList.remove('is--temp');

      const url = await this.getWistiaPlaceholderUrl(this.video);

      if (url) {
        placeholderImage.src = url;
      }
    }
  }

  async getWistiaPlaceholderUrl(videoId) {
    const jsonUrl = `https://fast.wistia.com/embed/medias/${videoId}.json`;
    const oembedUrl = `https://fast.wistia.com/oembed?url=https://home.wistia.com/medias/${videoId}`;

    try {
      const o = await fetch(oembedUrl).then(r => r.json());

      if (o.thumbnail_url) {
        return o.thumbnail_url;
      }
    } catch {}

    try {
      const res = await fetch(jsonUrl);

      if (!res.ok) {
        return null;
      }

      const data = await res.json();
      const media = data.media;

      if (media) {
        if (media.embedOptions && media.embedOptions.stillUrl) {
          return media.embedOptions.stillUrl;
        }

        const stillAsset = media.assets.find(a => a.type === 'StillImageFile' && a.url);

        if (stillAsset && stillAsset.url) {
          return stillAsset.url;
        }

        if (media.thumbnail && media.thumbnail.url) {
          return media.thumbnail.url;
        }
      }
    } catch {}

    return null;
  }

  trackWistiaPlaysInHeap(video, percentage) {
    if (typeof window.heap === 'undefined') {
      return;
    }

    const reportingObject = {
      nameOfVideo: video.name(),
      percentageReached: percentage,
      duration: (video.duration() / 60).toFixed(2) + ' minutes total'
    };

    window.heap.track("Wistia Video", reportingObject);
  };
}

customElements.define('wistia-video', WistiaVideo);
